import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { COLOR_CATALOG } from '../data/colorCatalog';
import { COLOR_COMPARISONS } from '../data/colorComparisons';
import { PALETTE_COLLECTIONS } from '../data/paletteCollections';
import { SITE_URL } from '../data/siteRoutes';

const SCRIPT_ID = 'search-content-jsonld';
const CONTRAST_SOURCE = 'https://www.w3.org/WAI/WCAG22/Understanding/contrast-minimum.html';
const CSS_SOURCE = 'https://www.w3.org/TR/css-color-4/';
type SchemaNode = Record<string, unknown>;
type ListedPage = { name: string; path: string; description?: string };

function breadcrumbs(pathname: string, items: ListedPage[]): SchemaNode {
  return {
    '@type': 'BreadcrumbList',
    '@id': SITE_URL + pathname + '#breadcrumb',
    itemListElement: [{ name: 'Home', path: '/' }, ...items].map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: SITE_URL + item.path,
    })),
  };
}

function collectionPage(pathname: string, name: string, description: string, pages: ListedPage[], options: { breadcrumb?: ListedPage[]; updated?: string; citation?: string[]; image?: string } = {}): SchemaNode {
  const url = SITE_URL + pathname;
  const page: SchemaNode = {
    '@type': 'CollectionPage',
    '@id': url + '#webpage',
    url,
    name,
    description,
    inLanguage: 'en',
    publisher: { '@type': 'Organization', name: 'Color Match', url: SITE_URL },
    mainEntity: { '@id': url + '#items' },
  };
  if (options.updated) page.dateModified = options.updated;
  if (options.citation) page.citation = options.citation;
  if (options.image) page.image = SITE_URL + options.image;
  if (options.breadcrumb) page.breadcrumb = { '@id': url + '#breadcrumb' };
  const list: SchemaNode = {
    '@type': 'ItemList',
    '@id': url + '#items',
    name,
    numberOfItems: pages.length,
    itemListElement: pages.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'WebPage',
        url: SITE_URL + item.path,
        name: item.name,
        ...(item.description ? { description: item.description } : {}),
      },
    })),
  };
  return {
    '@context': 'https://schema.org',
    '@graph': [page, list, ...(options.breadcrumb ? [breadcrumbs(pathname, options.breadcrumb)] : [])],
  };
}

/** Structured data describes the same finite collections and guides shown to readers. */
export function buildSearchContentSchema(pathname: string): SchemaNode | null {
  if (pathname === '/validation') {
    return {
      '@context': 'https://schema.org', '@graph': [
        { '@type': 'WebPage', '@id': SITE_URL + '/validation#webpage', url: SITE_URL + '/validation', name: '40 reproducible color and contrast checks', dateModified: '2026-09-14', mainEntity: { '@id': SITE_URL + '/validation#dataset' } },
        { '@type': 'Dataset', '@id': SITE_URL + '/validation#dataset', url: SITE_URL + '/validation', name: 'Color Match color-validation-v1', description: '28 independently calculated WCAG contrast references, 6 attributed CIEDE2000 reference pairs, and 6 labelled correction invariants, with actual and expected results.', version: 'color-validation-v1', dateModified: '2026-09-14', creator: { '@type': 'Organization', name: 'Color Match', url: SITE_URL }, isAccessibleForFree: true, citation: [CONTRAST_SOURCE, 'https://hajim.rochester.edu/ece/sites/gsharma/ciede2000/'], distribution: ['json', 'csv'].map(format => ({ '@type': 'DataDownload', encodingFormat: format === 'json' ? 'application/json' : 'text/csv', contentUrl: SITE_URL + '/data/color-validation-v1.' + format })) },
        breadcrumbs(pathname, [{ name: 'Calculation methods', path: '/methodology' }, { name: 'Validation data', path: pathname }]),
      ],
    };
  }
  const tools: Record<string, { name: string; description: string }> = {
    '/': { name: 'Color Match Color Wheel', description: 'Explore color harmonies, compare swatches and use the resulting palette in Studio.' },
    '/compare': { name: 'Color Match Color Difference Checker', description: 'Compare two colors with CIEDE2000, RGB, HSL and WCAG text contrast.' },
    '/contrast-checker': { name: 'Color Match Contrast Checker and Fixer', description: 'Check an opaque sRGB pair, find a passing foreground and export the declared contrast checks.' },
    '/link-contrast-checker': { name: 'Color Match Link Contrast Checker', description: 'Check link and body text on a background, including G183 distinction and underline choices.' },
    '/studio': { name: 'Color Match Palette Studio', description: 'Edit palette colors and roles, inspect text contrast and export design tokens.' },
  };
  const tool = tools[pathname];
  if (tool) return { '@context': 'https://schema.org', '@type': 'WebApplication', '@id': SITE_URL + pathname + '#application', url: SITE_URL + pathname, name: tool.name, description: tool.description, applicationCategory: 'DesignApplication', operatingSystem: 'Web browser', browserRequirements: 'JavaScript required for interaction', isAccessibleForFree: true, publisher: { '@type': 'Organization', name: 'Color Match', url: SITE_URL }, citation: [CONTRAST_SOURCE, CSS_SOURCE] };
  if (pathname === '/colors') {
    return collectionPage(pathname, 'Color names and HEX codes', `Browse ${COLOR_CATALOG.length} named reference colors and their HEX codes. Names outside standardized CSS keywords are descriptive conventions.`, COLOR_CATALOG.map(color => ({
      name: color.name + ' #' + color.hex,
      path: '/color/' + color.hex,
    })), { breadcrumb: [{ name: 'Color names and HEX codes', path: pathname }] });
  }

  if (pathname === '/color-palettes') {
    return collectionPage(pathname, 'Color Palettes with HEX Codes', 'Explore twelve curated color combinations across pastel, earth tone, neutral and dark mode palettes. Each example includes five HEX codes, suggested surface, text and accent roles, and calculated contrast for the reading pair and accent label.', PALETTE_COLLECTIONS.map(collection => ({
      name: collection.title,
      path: '/color-palettes/' + collection.slug,
      description: collection.description,
    })), { breadcrumb: [{ name: 'Color palettes', path: pathname }], citation: [CONTRAST_SOURCE] });
  }

  const collection = PALETTE_COLLECTIONS.find(item => pathname === '/color-palettes/' + item.slug);
  if (collection) {
    return collectionPage(pathname, collection.title + ' with HEX Codes', collection.answer, collection.palettes.map(palette => ({
      name: palette.name,
      path: '/palette/' + palette.colors,
      description: palette.note,
    })), {
      breadcrumb: [{ name: 'Color palettes', path: '/color-palettes' }, { name: collection.title, path: pathname }],
      updated: collection.updated,
      citation: [CONTRAST_SOURCE, CSS_SOURCE + '#hex-notation'],
      image: '/og/palettes/' + collection.slug + '.png',
    });
  }

  const comparison = COLOR_COMPARISONS.find(item => pathname === '/compare/' + item.slug);
  if (comparison) {
    const url = SITE_URL + pathname;
    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': url + '#webpage',
          url,
          name: comparison.title,
          description: comparison.answer,
          dateModified: comparison.updated,
          inLanguage: 'en',
          publisher: { '@type': 'Organization', name: 'Color Match', url: SITE_URL },
          citation: [CSS_SOURCE + '#named-colors', CONTRAST_SOURCE],
          image: SITE_URL + '/og/compare/' + comparison.slug + '.png',
          breadcrumb: { '@id': url + '#breadcrumb' },
        },
        breadcrumbs(pathname, [
          { name: 'Compare colors', path: '/compare' },
          { name: comparison.first.name + ' vs ' + comparison.second.name, path: pathname },
        ]),
      ],
    };
  }
  return null;
}

/** Keep outside Suspense so every pathname change follows RouteMetadata's reset. */
export default function SearchContentSchema() {
  const { pathname } = useLocation();
  useEffect(() => {
    const schema = buildSearchContentSchema(pathname);
    if (!schema) return;
    let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema).replace(/</g, '\\u003c');
    return () => script.remove();
  }, [pathname]);
  return null;
}
